import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Sparkles, ArrowRight } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * The usage meter, in credits. No tokens, no model names, no daily caps,  * plan, allowance, what's left, and when it resets.
 */
export default function CreditMeter({ compact = false }) {
  const { getAuthHeader } = useAuth();
  const [s, setS] = useState(null);

  useEffect(() => {
    axios.get(`${API}/credits/status`, getAuthHeader()).then(r => setS(r.data)).catch(() => setS(null));
  }, [getAuthHeader]);

  if (!s) return null;

  const pct = s.allowance ? Math.min(100, Math.round((s.used_this_cycle / s.allowance) * 100)) : 0;
  const hue = s.exhausted ? '#f87171' : s.low ? '#fbbf24' : '#34d399';
  const resets = s.renews_on
    ? new Date(s.renews_on).toLocaleDateString(undefined, { month: 'long', day: 'numeric' })
    : null;

  if (compact) {
    return (
      <Link to="/app/billing" data-testid="credit-meter-compact"
        className="flex items-center gap-2 text-xs text-slate-300 hover:text-white transition-colors">
        <span className="w-1.5 h-1.5 rounded-full" style={{ background: hue }} />
        {s.plan === 'trial'
          ? `Trial · ${s.remaining} credits · ${s.trial_days_left ?? 0} days left`
          : `${s.remaining.toLocaleString()} credits left`}
      </Link>
    );
  }

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
      className="glass-tint fp-rail p-5" data-testid="credit-meter">
      <div className="flex items-baseline justify-between gap-3">
        <div>
          <p className="text-[10px] uppercase tracking-[0.2em] text-slate-300">
            {s.plan === 'trial' ? 'FuturePath Trial' : `FuturePath ${s.plan_name}`}
          </p>
          <p className="font-outfit text-2xl font-semibold text-white mt-1.5 fp-num"
            data-testid="credit-meter-remaining">
            {s.remaining.toLocaleString()}
            <span className="text-sm font-normal text-slate-300">
              {' '}of {s.allowance.toLocaleString()} AI credits remaining
            </span>
          </p>
        </div>
        {s.plan === 'trial' && s.trial_days_left !== null && (
          <span className="text-xs text-slate-300">{s.trial_days_left} days left</span>
        )}
      </div>

      <div className="fp-meter mt-4">
        <div className="fp-meter-fill" style={{ width: `${100 - pct}%`, background: hue,
          boxShadow: `0 0 12px ${hue}66` }} />
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 mt-3">
        <p className="text-xs text-slate-300">
          {s.trial_expired ? 'Trial ended' : resets ? `Renews ${resets}` : 'Resets each cycle'}
          {s.purchased > 0 && ` · ${s.purchased.toLocaleString()} top-up credits held`}
        </p>
        {(s.low || s.trial_expired || s.plan === 'trial') && (
          <Link to="/pricing" data-testid="credit-meter-upgrade"
            className="text-xs inline-flex items-center gap-1.5 hover:gap-2.5 transition-all"
            style={{ color: hue }}>
            {s.exhausted || s.trial_expired ? 'Choose a plan' : 'See plans'} <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        )}
      </div>

      {s.message && (
        <div className="mt-4 pt-4 border-t border-white/10 flex gap-2.5" data-testid="credit-meter-message">
          <Sparkles className="w-4 h-4 text-cyan-300 shrink-0 mt-0.5" />
          <p className="text-sm text-slate-200/90">{s.message}</p>
        </div>
      )}
    </motion.div>
  );
}
